import React, { useState, useEffect } from 'react';
import {
  X,
  Folder,
  File,
  Download,
  ChevronRight,
  Home,
  Loader,
  FileText,
  FileCode,
  FileImage,
  FileVideo,
  FileArchive,
  FileSpreadsheet,
  AlertTriangle,
  HardDrive,
  ChevronUp,
  Eye,
  EyeOff
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import styles from './StorageContentsModal.module.css';

// File type icon mapping
const FILE_ICONS = {
  blender: FileText,
  freecad: FileText,
  kicad: FileText,
  jupyter: FileCode,
  code: FileCode,
  pdf: FileText,
  document: FileText,
  text: FileText,
  spreadsheet: FileSpreadsheet,
  image: FileImage,
  video: FileVideo,
  archive: FileArchive,
  file: File,
};

function StorageContentsModal({ onClose }) {
  const { theme } = useTheme();
  const [currentPath, setCurrentPath] = useState('');
  const [directories, setDirectories] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalSize, setTotalSize] = useState('');
  const [itemCount, setItemCount] = useState(0);
  const [showHidden, setShowHidden] = useState(false);

  useEffect(() => {
    fetchDirectoryContents(currentPath);
  }, [currentPath, showHidden]);

  const fetchDirectoryContents = async (path) => {
    setLoading(true);
    setError('');
    
    try {
      const showHiddenParam = showHidden ? '&show_hidden=true' : '';
      const queryPath = path ? `?path=${encodeURIComponent(path)}${showHiddenParam}` : `?show_hidden=${showHidden}`;
      const response = await fetch(`/v1/efs_storage/browse${queryPath}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.RetCode === 0) {
        setDirectories(data.data.directories || []);
        setFiles(data.data.files || []);
        setTotalSize(data.data.total_size_formatted || '0 B');
        setItemCount(data.data.item_count || 0);
      } else {
        // Storage might not be initialized yet
        if (data.retmsg && data.retmsg.includes('not initialized')) {
          setDirectories([]);
          setFiles([]);
          setTotalSize('0 B');
          setItemCount(0);
          setError(data.retmsg);
        } else {
          setError(data.retmsg || 'Failed to load directory contents');
        }
      }
    } catch (err) {
      console.error('Error fetching directory contents:', err);
      setError('Failed to load directory contents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const navigateToDirectory = (dirName) => {
    const newPath = currentPath ? `${currentPath}/${dirName}` : dirName;
    setCurrentPath(newPath);
  };

  const navigateUp = () => {
    const pathParts = currentPath.split('/').filter(p => p);
    pathParts.pop();
    setCurrentPath(pathParts.join('/'));
  };

  const navigateToRoot = () => {
    setCurrentPath('');
  };

  const navigateToBreadcrumb = (index) => {
    const pathParts = currentPath.split('/').filter(p => p);
    const newPath = pathParts.slice(0, index + 1).join('/');
    setCurrentPath(newPath);
  };

  const downloadFile = async (fileName) => {
    try {
      const filePath = currentPath ? `${currentPath}/${fileName}` : fileName;
      const downloadUrl = `/v1/efs_storage/download?path=${encodeURIComponent(filePath)}`;
      
      // Create a temporary link and click it to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file. Please try again.');
    }
  };

  const getFileIcon = (iconType) => {
    const IconComponent = FILE_ICONS[iconType] || File;
    return IconComponent;
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Unknown';
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  const renderBreadcrumbs = () => {
    if (!currentPath) {
      return (
        <div className={styles.breadcrumbs}>
          <button onClick={navigateToRoot} className={styles.homeButton}>
            <Home className="h-4 w-4" />
          </button>
          <span className={styles.breadcrumbButton}>Storage Root</span>
        </div>
      );
    }

    const pathParts = currentPath.split('/').filter(p => p);
    
    return (
      <div className={styles.breadcrumbs}>
        <button onClick={navigateToRoot} className={styles.homeButton}>
          <Home className="h-4 w-4" />
        </button>
        {pathParts.map((part, index) => (
          <React.Fragment key={index}>
            <ChevronRight className={`h-4 w-4 ${styles.breadcrumbSeparator} ${index === 0 ? styles.firstSeparator : ''}`} />
            <button
              onClick={() => navigateToBreadcrumb(index)}
              className={`${styles.breadcrumbButton} ${index < pathParts.length - 2 ? styles.truncateEarly : ''} ${index === pathParts.length - 1 ? styles.lastItem : ''}`}
            >
              {part}
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" style={{ zIndex: 10000 }}>
      <div className={`w-full max-w-6xl ${styles.modal} max-h-[80vh] flex flex-col`}>
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <div className={styles.headerIcon}>
                <HardDrive className="h-4 w-4" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Manage Server Storage</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Breadcrumb Navigation */}
        <div className={styles.navigationBar}>
          {renderBreadcrumbs()}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHidden(!showHidden)}
              className={styles.upButton}
              title={showHidden ? "Hide hidden files" : "Show hidden files"}
            >
              {showHidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </button>
            {currentPath && (
              <button
                onClick={navigateUp}
                className={styles.upButton}
                title="Move one folder up"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Info Bar */}
        <div className={styles.infoBar}>
          <span className="text-sm text-muted-foreground">
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
            {totalSize !== '0 B' && ` • ${totalSize}`}
          </span>
        </div>

        {/* Error Message */}
        {error && (
          <div className={styles.errorBanner}>
            <AlertTriangle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className={styles.loadingContainer}>
            <Loader className="h-6 w-6 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground ml-2">Loading...</span>
          </div>
        )}

        {/* Content Area */}
        {!loading && (
          <div className={styles.contentArea}>
            {/* Empty State */}
            {directories.length === 0 && files.length === 0 && !error && (
              <div className={styles.emptyState}>
                <Folder className={`h-12 w-12 opacity-50 mb-3 ${
                  theme === 'dark' ? 'text-cyan-400' : 'text-blue-500'
                }`} />
                <p className="text-muted-foreground">This directory is empty</p>
                <p className="text-sm text-muted-foreground">
                  Files created in your servers will appear here
                </p>
              </div>
            )}

            {/* Directories and Files */}
            {(directories.length > 0 || files.length > 0) && (
              <div className={styles.itemList}>
                {/* Directories */}
                {directories.map((dir) => (
                  <div
                    key={dir.name}
                    className={styles.item}
                    onClick={() => navigateToDirectory(dir.name)}
                  >
                    <Folder className={`h-5 w-5 ${
                      theme === 'dark' ? 'text-cyan-400' : 'text-blue-500'
                    }`} />
                    <div className={styles.itemDetails}>
                      <span className={styles.itemName}>{dir.name}</span>
                      <span className={styles.itemMeta}>
                        {dir.item_count} {dir.item_count === 1 ? 'item' : 'items'}
                      </span>
                    </div>
                    <span className={styles.itemDate}>{formatDate(dir.modified)}</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}

                {/* Files */}
                {files.map((file) => {
                  const IconComponent = getFileIcon(file.icon_type);
                  return (
                    <div key={file.name} className={styles.item}>
                      <IconComponent className="h-5 w-5 text-muted-foreground" />
                      <div className={styles.itemDetails}>
                        <span className={styles.itemName}>{file.name}</span>
                        <span className={styles.itemMeta}>{file.size_formatted}</span>
                      </div>
                      <span className={styles.itemDate}>{formatDate(file.modified)}</span>
                      <button
                        onClick={() => downloadFile(file.name)}
                        className={styles.downloadButton}
                        title="Download file"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <button
            onClick={onClose}
            className={styles.closeFooterButton}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default StorageContentsModal;

